import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, IconButton, Typography } from '@mui/material'
import dayjs from 'dayjs'
import theme from 'theme'

/* Utils */
import { IntegrationInstance, IntegrationStatusMessage, ManifestIntegration } from 'models'
import { escapeRegExp } from 'lib/utils'
import {
  INSTANCE_EDIT_DISABLED_HINT,
  getInstanceEditUrl,
  getVendorNameForInstance,
  instanceOfManifestIntegration,
  isInstanceEditDisabled,
  isIntegrationNew
} from '../utils'

/* Components */
import {
  IntegrationCardStyled,
  CardHeader,
  LogoContainer,
  NewChip,
  CategoryChip,
  ViewInstancesButton,
  NoInstancesText,
  CardContent,
  AddIconWrapper
} from './integrationCard.styles'
import { AddIconTooltip } from './AddIconTooltip'
import { AddIcon, EditIconDefault, NewChipIcon } from '../../../../components/svg'

interface Props {
  integration: ManifestIntegration | IntegrationInstance
  supportedIntegrations: ManifestIntegration[]
  handleOpenDrawer: (integration: ManifestIntegration | IntegrationInstance) => void
  searchValue: string
  configuredIntegrations: IntegrationInstance[]
  handleViewInstance: (integration: ManifestIntegration) => void
}

const IntegrationCard: FC<Props> = ({
  integration,
  supportedIntegrations,
  handleOpenDrawer,
  searchValue,
  configuredIntegrations,
  handleViewInstance
}) => {
  const navigate = useNavigate()

  const editDisabled = !instanceOfManifestIntegration(integration) && isInstanceEditDisabled(integration)

  const actionLabel = editDisabled
    ? INSTANCE_EDIT_DISABLED_HINT
    : instanceOfManifestIntegration(integration)
    ? 'Add Instance'
    : 'Edit Instance'

  /* Chips share one row and ellipsize, so the full category stays reachable on hover. */
  const renderCategoryChip = (category: string) => (
    <AddIconTooltip key={category} title={<Typography className="tooltip-text">{category}</Typography>}>
      <CategoryChip label={category} size="small" />
    </AddIconTooltip>
  )

  const renderInstanceCategories = () => {
    const thisIntegration = supportedIntegrations.find(
      (c) => c.name === (integration as IntegrationInstance).integration_name
    )
    if (!thisIntegration) return <></>
    return Array.isArray(thisIntegration.categories)
      ? thisIntegration.categories.map((category: any) => renderCategoryChip(category))
      : renderCategoryChip(thisIntegration.categories)
  }

  const highlightMatch = (text: string) => {
    if (!searchValue) return text

    const regex = new RegExp(`(${escapeRegExp(searchValue)})`, 'gi')
    return text.split(regex).map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ color: theme.colors.cta }}>
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  const integrationInstances = configuredIntegrations.filter((n) =>
    instanceOfManifestIntegration(integration) ? n.integration_name === integration.name : n.name === integration.name
  )

  const hasFailed = integrationInstances.find(
    (n) => n.syncStatus && n.syncStatus.status === IntegrationStatusMessage.failed
  )

  const getInstanceIcon = (name: string) => {
    if (name === 'knowbe4_phisher') {
      return `/icons/transparent/rounded/knowbe4_icon.svg`
    }
    if (name === 'checkpoint_harmony') {
      return `/icons/transparent/rounded/check_point.svg`
    }
    if (name === 'abnormal') {
      return `/icons/transparent/rounded/abnormalsecurity.png`
    }

    return `/icons/transparent/rounded/${name}_icon.svg`
  }

  return (
    <IntegrationCardStyled
      is_instance={instanceOfManifestIntegration(integration) ? 'false' : 'true'}
      className="integration-card"
      onClick={() => handleOpenDrawer(integration)}
    >
      <CardHeader className="card-header">
        <Box display="flex" gap="20px" sx={{ width: '100%', overflow: 'hidden' }}>
          <LogoContainer className="logo-container">
            {instanceOfManifestIntegration(integration) ? (
              <img
                src={
                  integration.name === 'crowdstrike_identity_iam' || integration.name === 'crowdstrike_cspm'
                    ? `/icons/transparent/rounded/crowdstrike_identity_iam_icon.svg`
                    : `/icons/transparent/rounded/${integration.logo_path}`
                }
                alt={`${integration.logo_path}`}
                width="56px"
                height="56px"
              />
            ) : (
              <img
                src={getInstanceIcon(integration.integration_name)}
                alt={integration.name}
                width="56px"
                height="56px"
              />
            )}
          </LogoContainer>

          <Box sx={{ gap: '2px', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <AddIconTooltip
                title={
                  <Typography className="tooltip-text">
                    {instanceOfManifestIntegration(integration)
                      ? integration.display_name
                      : integration.is_v2
                      ? integration.name || ''
                      : integration.name}
                  </Typography>
                }
              >
                <Typography variant="h6" className="integration-name">
                  {highlightMatch(
                    instanceOfManifestIntegration(integration)
                      ? integration.display_name
                      : integration.is_v2
                      ? integration.name || ''
                      : integration.name
                  )}
                </Typography>
              </AddIconTooltip>

              {hasFailed && !instanceOfManifestIntegration(integration) && (
                <AddIconTooltip
                  title={
                    <Box className="error-wrapper-instance">
                      <Typography className="tooltip-text">Data Syncronization Failure</Typography>
                      <Typography className="tooltip-description">
                        The data synchronization for the data source has encountered a failure. Please review the
                        integration and API key configurations to resolve the issue.
                      </Typography>
                    </Box>
                  }
                >
                  <Box className="instance-error-wrapper">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.26725 2.00251C7.0369 0.668435 8.96233 0.668435 9.73199 2.00251L14.6349 10.5008C15.4041 11.8342 14.4418 13.5003 12.9025 13.5003H3.09673C1.55742 13.5003 0.59513 11.8342 1.36436 10.5008L6.26725 2.00251ZM7.99976 5.50024C8.2759 5.50024 8.49976 5.7241 8.49976 6.00024V8.50024C8.49976 8.77639 8.2759 9.00024 7.99976 9.00024C7.72361 9.00024 7.49976 8.77639 7.49976 8.50024V6.00024C7.49976 5.7241 7.72361 5.50024 7.99976 5.50024ZM7.99976 11.0002C8.2759 11.0002 8.49976 10.7764 8.49976 10.5002C8.49976 10.2241 8.2759 10.0002 7.99976 10.0002C7.72361 10.0002 7.49976 10.2241 7.49976 10.5002C7.49976 10.7764 7.72361 11.0002 7.99976 11.0002Z"
                        fill="#E74C3C"
                      />
                    </svg>
                  </Box>
                </AddIconTooltip>
              )}
            </Box>
            <Typography variant="body2" className="instance-name">
              {highlightMatch(
                instanceOfManifestIntegration(integration)
                  ? integration.vendor.display_name
                  : getVendorNameForInstance(integration.integration_name, supportedIntegrations)
              )}
            </Typography>

            {!instanceOfManifestIntegration(integration) && (
              <AddIconTooltip title={<Typography className="tooltip-text">{integration.description}</Typography>}>
                <Typography variant="body2" className="instance-description">
                  {integration.description}
                </Typography>
              </AddIconTooltip>
            )}

            {isIntegrationNew(integration) && (
              <NewChip>
                <NewChipIcon />
                New
              </NewChip>
            )}
          </Box>
        </Box>

        <AddIconWrapper
          isdisabled={editDisabled ? 'true' : 'false'}
          onClick={() => {
            if (editDisabled) return

            const url = instanceOfManifestIntegration(integration)
              ? `/settings/integrations/data-sources/${integration?.name}/add`
              : getInstanceEditUrl(integration)

            navigate(url)
          }}
        >
          <AddIconTooltip title={<Typography className="tooltip-text">{actionLabel}</Typography>}>
            {/* Kept enabled so the tooltip still opens on hover; a disabled MUI
              * button drops pointer events and would swallow it. */}
            <IconButton aria-disabled={editDisabled} disableFocusRipple disableRipple disableTouchRipple>
              {instanceOfManifestIntegration(integration) ? <AddIcon /> : <EditIconDefault className="edit-icon" />}
            </IconButton>
          </AddIconTooltip>
        </AddIconWrapper>
      </CardHeader>

      <CardContent
        className="card-content"
        hasInstances={instanceOfManifestIntegration(integration) && integration.instances > 0 ? 'true' : 'false'}
      >
        <Box className="categories-wrapper">
          {instanceOfManifestIntegration(integration) ? (
            <>
              {Array.isArray(integration.categories)
                ? integration.categories.map((category: any) => renderCategoryChip(category))
                : renderCategoryChip(integration.categories)}
            </>
          ) : (
            <>{renderInstanceCategories()}</>
          )}
        </Box>

        {instanceOfManifestIntegration(integration) && (
          <>
            {integration.instances > 0 ? (
              <ViewInstancesButton
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleViewInstance(integration)
                }}
              >
                View {integration.instances} {integration.instances === 1 ? 'Instance' : 'Instances'}
              </ViewInstancesButton>
            ) : (
              <NoInstancesText>No Configured Instances</NoInstancesText>
            )}
          </>
        )}

        {!instanceOfManifestIntegration(integration) && (
          <Box className="instance-footer">
            <Typography className="footer-created-at-text">
              Created: <span>{dayjs(integration.created_at).format('MM/DD/YYYY')}</span>
            </Typography>
            {/*            <Typography className="footer-last-sync-text">
              Last Sync: <span>{dayjs(integration.updated_at).format('MM/DD/YYYY')}</span>
            </Typography> */}
          </Box>
        )}
      </CardContent>
    </IntegrationCardStyled>
  )
}

export default IntegrationCard
